import { useState } from 'react';

import { useExtendApiKey, useExtendMcpToken } from '@/api/queries';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { formatUtcDate } from '@/ui/format';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { ExpiryField, isDayInRange } from './ExpiryField';
import type { CredentialKind } from './CreateTokenDialog';
import { DEFAULT_DAYS, earliestDay, latestDay, utcDay } from './mcpScopes';

/** What the dialog needs of a personal token or an API key. */
interface Extendable {
  id: string;
  name: string;
  expires_at: string;
}

export function ExtendTokenDialog({ token, onClose, kind = 'token' }: { token: Extendable; onClose: () => void; kind?: CredentialKind }) {
  const extendToken = useExtendMcpToken();
  const extendKey = useExtendApiKey();
  const extend = kind === 'key' ? extendKey : extendToken;
  const noun = kind === 'key' ? 'key' : 'token';
  // The server wants a day after the one the token works through now (it ends at the close of that day).
  const current = token.expires_at.slice(0, 10);
  const min = [earliestDay(), utcDay(1, current)].sort().pop() ?? earliestDay();
  const max = latestDay();
  const reachable = min <= max;
  const [day, setDay] = useState(() => [utcDay(DEFAULT_DAYS, current), max].sort()[0] ?? max);
  const valid = reachable && isDayInRange(day, min, max);
  const error = reachable
    ? `Pick a date from ${formatUtcDate(`${min}T00:00:00Z`)} to ${formatUtcDate(`${max}T00:00:00Z`)}.`
    : `This ${noun} already works through ${formatUtcDate(token.expires_at)} UTC, so it cannot be extended.`;

  return (
    <Modal
      title={`Extend ${token.name}`}
      size="narrow"
      onClose={onClose}
      onSubmit={() => valid && extend.mutate({ id: token.id, expires_on: day }, { onSuccess: onClose })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <ActionButton state={buttonState(extend)} label={`Extend ${noun}`} pendingLabel="Extending…" doneLabel="Extended" disabled={!valid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={extend.error} />
        <ExpiryField
          id="credential-extend-on"
          label="Expires on"
          tip={`Must end later than ${formatUtcDate(token.expires_at)} and up to 90 days from today, in UTC. It keeps working until the end of that day.`}
          value={day}
          min={min}
          max={max}
          error={error}
          autoFocus
          onChange={setDay}
        />
      </div>
    </Modal>
  );
}
